import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Sortable from "sortablejs";

// HEIC/HEIF are accepted but only Safari can decode one for the downscale
// below; elsewhere the original uploads untouched and the server converts it.
const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];
// Types a browser renders directly, so a file already this small and this size
// is left exactly as it is rather than re-encoded for no gain.
const NATIVE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const PASSTHROUGH_BYTES = 2 * 1024 * 1024;
// The biggest thing ever rendered is the 2000px lightbox variant, so shrinking
// to this before upload is invisible and turns a 20 MB camera file into ~1.5 MB.
const MAX_EDGE = 2560;
const JPEG_QUALITY = 0.85;

const jpegName = (name) => `${name.replace(/\.[^.]+$/, "")}.jpg`;

// Owner gallery manager for property photos. Each dropped file direct-uploads
// to storage (mirroring dropzone_controller), then a PropertyPhoto record is
// created out-of-band via the photos endpoint — the surrounding property form
// never carries the files. Tiles offer an inline title (saved on blur/Enter),
// a drag handle (SortableJS reorder PATCHes the 1-based position), and delete.
// The first tile is the cover; the badge follows position 1 via CSS.
export default class extends Controller {
  static targets = ["input", "grid", "tileTemplate"];
  static values = {
    photos: { type: Array, default: [] }, // {id, thumbUrl, title, updateUrl, deleteUrl}
    directUploadUrl: String,
    createUrl: String,
    maxFilesize: { type: Number, default: 20 }, // MB
  };

  connect() {
    this.photosValue.forEach((photo) => {
      const tile = this.buildTile({
        thumbUrl: photo.thumbUrl,
        title: photo.title,
      });
      this.markSaved(tile, photo);
    });

    // forceFallback: Sortable's native HTML5 DnD mode is unreliable from a
    // <button> handle and collides with this controller's file-drop handlers;
    // the synthetic (mouse-event) mode is what raven ships too.
    this.sortable = Sortable.create(this.gridTarget, {
      animation: 150,
      handle: ".photo-tile-handle",
      ghostClass: "photo-tile-ghost",
      forceFallback: true,
      fallbackTolerance: 3,
      onEnd: (event) => this.reorder(event),
    });
  }

  disconnect() {
    this.sortable?.destroy();
  }

  browse() {
    this.inputTarget.click();
  }

  inputChanged() {
    [...this.inputTarget.files].forEach((file) => this.upload(file));
    this.inputTarget.value = "";
  }

  dragover(event) {
    if (!this.draggingFiles(event)) return;
    event.preventDefault();
    this.element.classList.add("drag-hover");
  }

  dragleave(event) {
    if (!this.element.contains(event.relatedTarget)) {
      this.element.classList.remove("drag-hover");
    }
  }

  drop(event) {
    if (!this.draggingFiles(event)) return;
    event.preventDefault();
    this.element.classList.remove("drag-hover");
    [...event.dataTransfer.files].forEach((file) => this.upload(file));
  }

  // Only OS file drags concern the drop area — tile reorders (and any other
  // in-page drag) pass through untouched.
  draggingFiles(event) {
    return [...(event.dataTransfer?.types || [])].includes("Files");
  }

  async upload(file) {
    const tile = this.buildTile({
      thumbUrl: URL.createObjectURL(file),
      title: "",
    });

    if (!this.accepted(file)) {
      this.markFailed(tile, "JPG, PNG, WebP, or HEIC only");
      return;
    }
    if (file.size > this.maxFilesizeValue * 1024 * 1024) {
      this.markFailed(tile, `Larger than ${this.maxFilesizeValue} MB`);
      return;
    }

    // The size check above applies to what the camera produced; the upload
    // itself carries the downscaled copy.
    const uploadable = await this.downscaled(file);

    const fill = tile.querySelector(".photo-tile-progress span");
    const delegate = {
      directUploadWillStoreFileWithXHR(xhr) {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable)
            fill.style.width = `${Math.round((event.loaded / event.total) * 100)}%`;
        });
      },
    };

    new DirectUpload(uploadable, this.directUploadUrlValue, delegate).create(
      (error, blob) => {
        if (error) {
          this.markFailed(tile, "Upload failed — remove and retry");
        } else {
          this.createRecord(tile, blob.signed_id);
        }
      },
    );
  }

  accepted(file) {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    // Some browsers hand back an empty type for .heic/.heif.
    return file.type === "" && /\.hei[cf]$/i.test(file.name);
  }

  // Shrinks an image to MAX_EDGE and re-encodes it as JPEG before it goes over
  // the wire. Returns the original file whenever re-encoding would not help:
  // it is already web-sized, the browser can't decode it (HEIC outside Safari,
  // where the server takes over), or the JPEG came out no smaller.
  async downscaled(file) {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, {
        imageOrientation: "from-image",
      });
      const scale = Math.min(
        MAX_EDGE / bitmap.width,
        MAX_EDGE / bitmap.height,
        1,
      );
      const worthDoing =
        scale < 1 ||
        file.size > PASSTHROUGH_BYTES ||
        !NATIVE_TYPES.includes(file.type);
      if (!worthDoing) return file;

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      const context = canvas.getContext("2d");
      // JPEG has no alpha channel; without a matte, transparent PNG pixels
      // would come out black.
      context.fillStyle = "#fff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
      );
      if (!blob || blob.size >= file.size) return file;

      return new File([blob], jpegName(file.name), { type: "image/jpeg" });
    } catch {
      return file;
    } finally {
      bitmap?.close();
    }
  }

  async createRecord(tile, signedBlobId) {
    const response = await this.request(this.createUrlValue, "POST", {
      signed_blob_id: signedBlobId,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      this.markFailed(
        tile,
        (body.errors || ["Couldn't save the photo"]).join(", "),
      );
      return;
    }
    const photo = await response.json();
    this.markSaved(tile, {
      updateUrl: photo.update_url,
      deleteUrl: photo.delete_url,
    });
  }

  buildTile({ thumbUrl, title }) {
    const tile =
      this.tileTemplateTarget.content.firstElementChild.cloneNode(true);
    tile.querySelector(".photo-tile-image img").src = thumbUrl;
    const titleInput = tile.querySelector(".photo-tile-title");
    titleInput.value = title || "";
    titleInput.disabled = true; // enabled once the record exists
    this.gridTarget.appendChild(tile);
    return tile;
  }

  markSaved(tile, photo) {
    tile.dataset.updateUrl = photo.updateUrl;
    tile.dataset.deleteUrl = photo.deleteUrl;
    tile.querySelector(".photo-tile-progress").remove();
    tile.querySelector(".photo-tile-title").disabled = false;
  }

  markFailed(tile, message) {
    tile.classList.add("photo-tile-failed");
    tile.querySelector(".photo-tile-progress").remove();
    const error = document.createElement("div");
    error.className = "photo-tile-error small";
    error.textContent = message;
    tile.querySelector(".photo-tile-title").replaceWith(error);
  }

  async saveTitle(event) {
    const tile = event.target.closest(".photo-tile");
    const title = event.target.value.trim();
    if (!tile.dataset.updateUrl || title === (tile.dataset.savedTitle || ""))
      return;

    const response = await this.request(tile.dataset.updateUrl, "PATCH", {
      title,
    });
    if (response.ok) {
      tile.dataset.savedTitle = title;
      event.target.classList.add("photo-tile-title-saved");
      setTimeout(
        () => event.target.classList.remove("photo-tile-title-saved"),
        1200,
      );
    }
  }

  titleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault(); // don't submit the surrounding property form
      event.target.blur(); // blur triggers saveTitle
    }
  }

  reorder(event) {
    const url = event.item.dataset.updateUrl;
    if (!url || event.newIndex === event.oldIndex) return;
    this.request(url, "PATCH", { position: event.newIndex + 1 });
  }

  async remove(event) {
    const tile = event.target.closest(".photo-tile");
    if (tile.dataset.deleteUrl) {
      const response = await this.request(tile.dataset.deleteUrl, "DELETE");
      if (!response.ok) return;
    }
    tile.remove();
  }

  request(url, method, body = null) {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.content || "",
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : null,
    });
  }
}
